import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Gilu customization: the cart drawer's "Complete your look" cross-sell,
 * rendered by `snippets/cart-complementary-products.liquid`.
 *
 * The snippet renders one hidden group per cart product; this module decides
 * which one is visible. It remembers the variant of the latest `add` cart
 * event from any source (product form, quick add, look builder, this
 * carousel) and shows the group whose cart variants include it. Liquid order
 * cannot express "added last" when an add only raises an existing line's
 * quantity, which is why this lives client-side. The choice is kept in
 * sessionStorage so the drawer on the next page still matches.
 *
 * Adding follows look-builder.js: it announces a CartLinesUpdateEvent, posts
 * to the Cart API asking for the cart sections, and resolves the event with
 * the fresh cart. The drawer's cart-items-component then morphs itself, which
 * also re-renders this carousel, and the drawer stays open because its dialog
 * lives outside the morphed subtree.
 */

const STORAGE_KEY = 'gilu:cart-complementary-last-added-variant';

/**
 * Numeric variant id of the most recent add, or null when unknown.
 * @type {string | null}
 */
let lastAddedVariantId = readStoredVariantId();

/** @returns {string | null} */
function readStoredVariantId() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Standard events convert `merchandiseId` to a `gid://shopify/ProductVariant/…`
 * string; theme callers pass plain ids. Both reduce to the numeric tail.
 * @param {unknown} merchandiseId
 * @returns {string | null}
 */
function toVariantId(merchandiseId) {
  const id = String(merchandiseId ?? '').split('/').pop() ?? '';
  return /^\d+$/.test(id) ? id : null;
}

/**
 * Registered at module level, not per component: the carousel is absent while
 * the cart is empty, and the add that fills it must still be recorded.
 * @param {Event} event
 */
function rememberLastAdded(event) {
  const { action, lines } = /** @type {{action?: string, lines?: {merchandiseId?: unknown}[]}} */ (
    /** @type {unknown} */ (event)
  );
  if (action !== 'add' || !Array.isArray(lines) || lines.length === 0) return;

  // A multi-line add (a look-builder set) is referenced by its last line.
  const variantId = toVariantId(lines[lines.length - 1]?.merchandiseId);
  if (!variantId) return;

  lastAddedVariantId = variantId;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, variantId);
  } catch {
    // Storage can be unavailable (private mode, blocked site data); the
    // in-memory value still drives this page.
  }

  for (const carousel of document.querySelectorAll('cart-complementary-products-component')) {
    if (carousel instanceof CartComplementaryProductsComponent) carousel.showLastAddedGroup();
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, rememberLastAdded);

/**
 * @typedef {object} Refs
 * @property {HTMLElement} errorMessage - Inline error live region.
 *
 * @extends {Component<Refs>}
 */
class CartComplementaryProductsComponent extends Component {
  requiredRefs = ['errorMessage'];

  #busy = false;

  /**
   * The drawer re-renders by morphing, which restores the server's `hidden`
   * attributes on groups this element already has. Watching the subtree puts
   * the last-added group back after every morph.
   */
  #observer = new MutationObserver(() => this.showLastAddedGroup());

  connectedCallback() {
    super.connectedCallback();
    this.showLastAddedGroup();
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-variant-ids'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
  }

  /**
   * Shows the group of the last added variant, or the server's default group
   * (the first cart line) when that variant is unknown or no longer in the
   * cart. Idempotent, so the observer it triggers settles immediately.
   */
  showLastAddedGroup() {
    const groups = Array.from(this.querySelectorAll('[data-cart-complementary-group]'));
    if (groups.length === 0) return;

    const key = lastAddedVariantId ? `,${lastAddedVariantId},` : null;
    const active =
      (key && groups.find((group) => group instanceof HTMLElement && group.dataset.variantIds?.includes(key))) ||
      groups.find((group) => group.hasAttribute('data-default'));

    for (const group of groups) {
      if (!(group instanceof HTMLElement)) continue;
      const hide = group !== active;
      if (group.hidden !== hide) group.hidden = hide;
    }
  }

  /**
   * Mirrors the chosen variant's price on its card.
   * @param {Event} event
   */
  updatePrice(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const price = select.closest('[data-cart-complementary-card]')?.querySelector('[data-cart-complementary-price]');
    const formatted = select.selectedOptions[0]?.dataset.price;
    if (price && formatted) price.textContent = formatted;
  }

  /**
   * Adds one unit of the card's variant: the selected option for products with
   * variants, the button's own variant otherwise.
   * @param {Event} event
   */
  async addItem(event) {
    if (this.#busy) return;

    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const select = button.closest('[data-cart-complementary-card]')?.querySelector('[data-cart-complementary-variant]');
    const variantId = select instanceof HTMLSelectElement ? select.value : button.dataset.variantId;
    if (!variantId) return;

    const lines = [{ merchandiseId: variantId, quantity: 1 }];

    const sectionIds = [];
    for (const cartItems of document.querySelectorAll('cart-items-component')) {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionIds.push(cartItems.dataset.sectionId);
      }
    }

    this.#setBusy(button, true);
    this.#showError(null);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines,
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        this.#showError(result.description || result.message || this.dataset.errorFallback || '');
        // Rejected rather than resolved with `didError`: without sections in
        // the detail, cart-items-component re-renders the drawer, and that
        // morph would wipe the error message before anyone could read it.
        // Nothing changed in the cart, so there is nothing to refresh.
        deferred.reject(new Error(result.message || 'Add to cart failed'));
        return;
      }

      this.#resolveWithCart(deferred, result.sections);
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(this.dataset.errorFallback || '');
    } finally {
      this.#setBusy(button, false);
    }
  }

  /**
   * Fetches the post-mutation cart and resolves the deferred event promise the
   * cart drawer and cart items components are waiting on.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferred
   * @param {Record<string, string> | undefined} sections
   */
  #resolveWithCart(deferred, sections) {
    this.#fetchCart()
      .then((ajaxCart) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: false,
            items: ajaxCart.items,
            source: 'cart-complementary-products-component',
            itemCount: 1,
            sections,
          },
        });
      })
      .catch(deferred.reject);
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const cartItemsComponent = this.closest('cart-items-component') ?? document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItemsComponent).fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Locks every add button while one request is in flight, so two quick taps
   * cannot race two cart mutations and two drawer re-renders.
   * @param {HTMLButtonElement} activeButton
   * @param {boolean} busy
   */
  #setBusy(activeButton, busy) {
    this.#busy = busy;

    for (const button of this.querySelectorAll('.cart-complementary__add')) {
      if (button instanceof HTMLButtonElement) button.disabled = busy;
    }

    activeButton.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /** @param {string | null} message */
  #showError(message) {
    const { errorMessage } = this.refs;
    errorMessage.textContent = message ?? '';
    errorMessage.toggleAttribute('hidden', !message);
  }
}

if (!customElements.get('cart-complementary-products-component')) {
  customElements.define('cart-complementary-products-component', CartComplementaryProductsComponent);
}
